import React from 'react'
import { Link } from 'react-router-dom'
import { getSession } from '../lib/session'
import SiteFooter from '../components/SiteFooter'
import '../components/marketing-dark.css'

// Public product guide — mirrors docs/user-guide.md. No login required.
const MODELS = [
  ['Economy', 'Everyday Q&A', '1×', 'included'],
  ['Standard', 'Sharper answers', '3×', ''],
  ['Premium', 'Complex questions', '10×', ''],
  ['Frontier', 'The hardest questions', '20×', ''],
]
const SERVICES = [
  ['Voice AI Receptionist', 'Answers your phone by voice, routes the caller to the right department, and escalates to a human — in the caller’s own language.', 'Point a number’s Voice webhook → /api/v1/voice/incoming'],
  ['Knowledge Sync', 'An always-fresh, searchable copy of your content for your own apps.', 'POST /api/v1/sync/search'],
  ['Custom RAG API', 'A private “chat with your docs” query API over a dedicated collection.', 'POST /api/v1/rag/collections/{id}/query'],
  ['Agentic Workflows', 'Automations like “escalate to a human” or “capture a lead,” built from your content.', 'POST /api/v1/orchestration/workflows/{id}/run'],
  ['Integration Adapters', 'Turn inbound webhooks (forms, other tools) into your AI pipeline.', 'POST /api/v1/middleware/webhook/{adapterId}'],
  ['Multi-Specialty Intake', 'Routes inbound questions to the right desk — specialties auto-detected from your content.', 'POST /api/v1/intake/instances/{id}/intake'],
  ['Tool-using Agent / MCP', 'Drive it from Claude, Cursor, the CLI, or REST; it answers from your knowledge and can act through your tools.', 'POST /api/v1/agent/run'],
]

const TOC = [
  ['start', 'Getting started'], ['pricing', 'Pricing'], ['models', 'Models & credits'],
  ['buy', 'Buy & enable a service'], ['sources', 'Instances & data sources'],
  ['chatbot', 'The support chatbot'], ['channels', 'Phone, Slack & WhatsApp'],
  ['services', 'The other services'], ['mcp', 'Connect via MCP'], ['secrets', 'Secrets & variables'],
  ['faq', 'Quick answers'],
]

function Step({ n, children }) {
  return (
    <li className="relative pl-11 pb-5 last:pb-0">
      <span className="absolute left-0 top-0 w-7 h-7 rounded-full bg-primary-600 text-white text-sm font-bold flex items-center justify-center">{n}</span>
      <div className="pt-0.5">{children}</div>
    </li>
  )
}
function H2({ id, n, children }) {
  return (
    <h2 id={id} className="scroll-mt-24 flex items-center gap-3 text-2xl font-bold text-gray-900 mb-4">
      <span className="text-sm font-bold text-primary-600 bg-primary-500/10 border border-primary-500/25 rounded-md px-2 py-0.5">{n}</span>
      {children}
    </h2>
  )
}

export default function Guide() {
  const authed = !!getSession()?.apiKey
  return (
    <div className="mkt min-h-screen bg-gray-50 text-gray-900">
      {/* top bar */}
      <header className="mkt-frost sticky top-0 z-20 border-b border-gray-200 bg-gray-50/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="flex items-baseline font-display font-black text-xl leading-none">
              <span className="text-gray-900">R</span><span className="mx-0.5 text-gray-300 font-normal">|</span>
              <span className="text-primary-600 inline-block" style={{ transform: 'scaleX(-1)' }}>R</span>
            </span>
            <span className="font-display font-black tracking-tight text-gray-900">RERIGHT</span>
            <span className="text-[10px] uppercase tracking-[0.14em] text-gray-500 border-l border-gray-200 pl-2.5 ml-1">User Guide</span>
          </Link>
          <Link to={authed ? '/' : '/'} className="btn-primary text-sm px-4 py-2">{authed ? 'Open console' : 'Sign in'}</Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-10">
        {/* lede */}
        <div className="mb-10">
          <p className="text-xs font-bold tracking-[0.14em] uppercase text-primary-600">Getting the most out of ReRight</p>
          <h1 className="text-3xl sm:text-4xl font-display font-black tracking-tight text-gray-900 mt-2 mb-3" style={{ textWrap: 'balance' }}>
            Turn your website into AI services — one switch at a time.
          </h1>
          <p className="text-lg text-gray-500 max-w-2xl">A support chatbot for your site, a search API, automations, and more. You only pay for what you turn on.</p>
          <div className="flex flex-wrap items-center gap-2 mt-5 text-sm font-semibold">
            {['Sign in', 'Pick a service', 'Choose a model', 'Pay', 'It goes live'].map((s, i, a) => (
              <React.Fragment key={s}>
                <span className={`rounded-full px-3 py-1.5 border ${s === 'Pay' ? 'border-primary-600 text-primary-600' : 'border-gray-200 bg-white'} shadow-sm`}>{s}</span>
                {i < a.length - 1 && <span className="text-gray-400">→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-[200px_1fr] gap-10">
          {/* toc */}
          <nav className="hidden lg:block self-start sticky top-20">
            <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold mb-2">Contents</p>
            <ol className="space-y-0.5">
              {TOC.map(([id, label], i) => (
                <li key={id}>
                  <a href={`#${id}`} className="flex gap-2 text-sm text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md px-2 py-1.5">
                    <span className="text-primary-600 font-bold tabular-nums">{i + 1}</span>{label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {/* content */}
          <main className="max-w-2xl space-y-12">
            <section>
              <H2 id="start" n="1">Getting started</H2>
              <p className="mb-4 text-gray-600">Two minutes, and free until you activate a service.</p>
              <ol className="mb-2">
                <Step n="1">Go to <b>ai.reright.io</b>.</Step>
                <Step n="2"><b>Sign in</b> with Google or email &amp; password — the same login as reright.io.</Step>
                <Step n="3">First time only: <b>Create a workspace</b> and name it. This provisions your own private, isolated space.</Step>
                <Step n="4">You land on the <b>Services</b> page — your catalog.</Step>
              </ol>
            </section>

            <section>
              <H2 id="pricing" n="2">Simple à-la-carte pricing</H2>
              <p className="mb-4 text-gray-600">No bundles to decode. <b>Pay per service, per month</b> — your bill is just the sum of what you turn on, and you can add or cancel any of them anytime.</p>
              <ul className="space-y-2 text-gray-600 text-sm mb-4">
                <li>• There are <b>15 services</b>, each <b>$19–$199/mo</b>. Browse them on the landing page and add what you need to your cart.</li>
                <li>• Some services run <b>more than one instance</b> — e.g. three chatbots for three different purposes. Each instance is one paid <b>seat</b>; buy as many as you like.</li>
                <li>• You create your <b>free workspace at checkout</b> — you only pay for the services in your cart.</li>
              </ul>
              <div className="rounded-xl bg-primary-500/5 border border-primary-500/20 px-4 py-3 text-sm text-gray-600">
                <b>Instances = seats.</b> Running the same service on two different data sources (or for two purposes) is two seats. Add another anytime from the service card.
              </div>
            </section>

            <section>
              <H2 id="models" n="3">AI models &amp; credits</H2>
              <p className="mb-4 text-gray-600">Each service uses an <b>AI model</b> you choose. Better models answer better but spend your monthly message credits faster.</p>
              <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="grid grid-cols-[1fr_1fr_auto] gap-4 px-4 py-2.5 bg-gray-100 text-[11px] uppercase tracking-wide text-gray-500 font-bold">
                  <span>Model</span><span>Best for</span><span>Credits / msg</span>
                </div>
                {MODELS.map(([m, best, w, tag]) => (
                  <div key={m} className="grid grid-cols-[1fr_1fr_auto] gap-4 px-4 py-3 text-sm border-t border-gray-200">
                    <span className="font-bold text-gray-900">{m}{tag && <span className="text-gray-400 font-normal"> · {tag}</span>}</span>
                    <span className="text-gray-600">{best}</span><span className="font-semibold tabular-nums text-right">{w}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-gray-600 text-sm">Roughly: 5,000 credits ≈ 5,000 Economy replies <b>or</b> 500 Premium replies. Start on Economy; move up when you want more polish.</p>
            </section>

            <section>
              <H2 id="buy" n="4">How to buy &amp; enable a service</H2>
              <p className="mb-4 text-gray-600">The same for every service.</p>
              <ol className="mb-4">
                <Step n="1">On <b>Services</b>, find the service and click <b>Activate</b>.</Step>
                <Step n="2"><b>Pick the data source</b> — reuse an <b>existing source</b> (share its knowledge) or connect a <b>new source</b>. That’s what the service works from.</Step>
                <Step n="3"><b>Choose an AI model</b> (Economy by default).</Step>
                <Step n="4">Click <b>Continue to payment</b>.</Step>
                <Step n="5">Review the order and click <b>Pay &amp; activate</b>. It provisions itself and goes live; content services auto-refresh daily.</Step>
              </ol>
              <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-700">
                <b>Pre-launch:</b> card payments run in <b>test mode</b> — the flow is fully real, but nothing is charged yet.
              </div>
            </section>

            <section>
              <H2 id="sources" n="5">Instances &amp; data sources</H2>
              <p className="mb-4 text-gray-600">An <b>instance</b> is one <b>data source</b> — a body of knowledge. Every service you turn on for it shares that knowledge, so a chatbot, a search API, and an intake desk on the same source all stay in sync. Spin up a new instance only for a genuinely different source.</p>
              <h3 className="text-lg font-bold text-gray-900 mt-5 mb-2">A source can be almost anything</h3>
              <p className="text-gray-600 text-sm mb-3">A website URL is just one option. From the <b>Instances</b> page (or a service’s <b>Train</b> tab) connect any of:</p>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-gray-600 mb-4">
                {['Website URL / sitemap', 'Paste text', 'Upload a file (PDF, Word, Excel, CSV…)', 'SQL database (Postgres, MySQL, SQL Server — AWS RDS, Azure SQL, GCP Cloud SQL)', 'Cloud storage (S3, Google Cloud, Azure Blob)', 'Confluence · Jira · monday.com · Asana', 'Salesforce · SAP S/4HANA', 'Slack channels'].map((s) => <li key={s}>• {s}</li>)}
              </ul>
              <p className="text-gray-600 text-sm">Use <b>Test connection</b> before importing. Everything is encrypted; content lands in that instance’s knowledge and every service on it can use it.</p>
            </section>

            <section>
              <H2 id="chatbot" n="6">The AI Support Chatbot</H2>
              <p className="mb-4 text-gray-600">A trained bot that lives on <b>your</b> site as a chat bubble and answers visitors from your content.</p>

              <div className="rounded-xl bg-primary-500/5 border border-primary-500/20 px-4 py-3 text-sm text-gray-600 mb-5">
                <b>Grounded, verified &amp; multilingual.</b> Every answer is checked against your content before it’s sent — an unsupported claim is caught and replaced with a safe fallback, and you can see the quality score in <b>Governance → Trust</b>. It also detects the visitor’s language and replies in it automatically.
              </div>

              <h3 className="text-lg font-bold text-gray-900 mt-6 mb-2">Put it on your website — this is what makes it live</h3>
              <ol className="mb-4">
                <Step n="1">On the chatbot card: <b>Open chat → Install on your site</b>.</Step>
                <Step n="2">Copy the snippet:
                  <pre className="mt-2 bg-[#12151d] text-[#e6e8ec] border border-white/10 rounded-lg p-3 text-xs overflow-x-auto"><code>&lt;script src="https://ai.reright.io/widget.js" data-bot="YOUR_BOT_ID" async&gt;&lt;/script&gt;</code></pre>
                </Step>
                <Step n="3">Paste it into your site's HTML before <code className="text-xs bg-gray-100 border border-gray-200 rounded px-1">&lt;/body&gt;</code> — usually your site-wide layout or footer.</Step>
                <Step n="4"><b>Publish / redeploy your website.</b> The bubble now appears for your visitors.</Step>
              </ol>
              <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-700 mb-4">
                <b>Important:</b> until you paste the snippet into your site and redeploy, the bot is <b>not</b> on your site — it only exists in the ReRight console.
              </div>
              <h3 className="text-lg font-bold text-gray-900 mt-6 mb-2">Lock it to your domains</h3>
              <p className="text-gray-600 text-sm">In <b>Install → Allowed domains</b>, your site's domain is added automatically. The bot only answers on domains in this list, so nobody can run your bot on another site. It also re-reads your site <b>daily</b> to stay fresh.</p>
            </section>

            <section>
              <H2 id="channels" n="7">Answer by phone, Slack &amp; WhatsApp</H2>
              <p className="mb-4 text-gray-600">Your bot doesn’t have to live only on your website. Connect it to where your customers already are — same knowledge, same guardrails.</p>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li><b>Phone (Voice AI Receptionist)</b> — activate the service, buy a voice number (e.g. SignalWire or Twilio), and point its Voice webhook at <code className="text-xs bg-gray-100 border border-gray-200 rounded px-1">/api/v1/voice/incoming</code>. Callers reach an AI that answers by voice, routes them to the right department, and hands off to a human — in the caller’s own language.</li>
                <li><b>Slack</b> — create a Slack app, point its Event Subscriptions at our webhook, paste the bot token + signing secret. Your bot answers in Slack.</li>
                <li><b>WhatsApp</b> — in your Meta app, set the webhook + verify token and paste your phone-number ID + access token. Customers message your number; the bot replies.</li>
              </ul>
            </section>

            <section>
              <H2 id="services" n="8">The other services</H2>
              <p className="mb-4 text-gray-600">Each uses the same <b>Activate → model → pay</b> flow. After activating, open the card's <b>“How to use”</b> for the exact API calls (they use your workspace API key).</p>
              <div className="space-y-3">
                {SERVICES.map(([name, desc, use]) => (
                  <div key={name} className="card">
                    <h4 className="font-bold text-gray-900">{name}</h4>
                    <p className="text-sm text-gray-500 mt-1 mb-2">{desc}</p>
                    <code className="text-xs bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5">{use}</code>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <H2 id="mcp" n="9">Connect via MCP</H2>
              <p className="mb-4 text-gray-600">The <b>Model Context Protocol</b> connects AI agents to tools — and ReRight works in <b>both directions</b>.</p>
              <h3 className="text-lg font-bold text-gray-900 mt-5 mb-2">Use your workspace from Claude / Cursor</h3>
              <p className="text-gray-600 text-sm mb-2">We expose an MCP server, so your own agent can search and ask your knowledge base as tools. Point your client at:</p>
              <pre className="bg-[#12151d] text-[#e6e8ec] border border-white/10 rounded-lg p-3 text-xs overflow-x-auto mb-2"><code>{`{ "mcpServers": { "reright": {
  "url": "https://ai.reright.io/api/v1/mcp",
  "headers": { "Authorization": "Bearer <your workspace API key>" } } } }`}</code></pre>
              <p className="text-gray-600 text-sm">(Generate the API key in <b>Workspace → API key</b>.)</p>
              <h3 className="text-lg font-bold text-gray-900 mt-6 mb-2">Give our agent your tools</h3>
              <p className="text-gray-600 text-sm">The reverse is optional: in <b>Workspace → Your MCP servers</b>, register your own MCP servers and our Tool-using Agent can call their tools during a run — alongside its built-in ones (send email, send SMS, Notion, calendar invites).</p>
            </section>

            <section>
              <H2 id="secrets" n="10">Secrets &amp; variables</H2>
              <p className="mb-4 text-gray-600">Store your own API keys and config in <b>Workspace → Secrets &amp; Variables</b>, encrypted at rest. Reference them anywhere as <code className="text-xs bg-gray-100 border border-gray-200 rounded px-1">{'${NAME}'}</code> — in connectors, workflows, and agent tools — and the server fills them in.</p>
              <p className="text-gray-600 text-sm">Values are used <b>server-side and never sent to the AI</b> unless you flip a per-variable <b>“expose to the AI”</b> toggle (with a clear warning). Keep passwords and keys off; reference them by name instead.</p>
            </section>

            <section>
              <H2 id="faq" n="11">Quick answers</H2>
              <dl className="border-t border-gray-200">
                {[
                  ['Is my chatbot live on my site?', 'Only after you paste the embed snippet (§5) into your site\'s HTML and redeploy your site.'],
                  ['The bot says it doesn\'t know something.', 'Ask a more specific question, or check that the page with that info is on the site you trained it on. It refreshes daily.'],
                  ['Can someone steal my bot?', 'Not onto another website — the domain allowlist blocks embeds on domains you didn\'t approve.'],
                  ['How do I change the AI model?', 'Re-activate the service and pick a different tier.'],
                  ['Was I charged?', 'Not yet — payments are in test mode until card billing goes live.'],
                ].map(([q, a]) => (
                  <div key={q} className="py-3 border-b border-gray-200">
                    <dt className="font-bold text-gray-900">{q}</dt>
                    <dd className="text-gray-500 text-sm mt-1">{a}</dd>
                  </div>
                ))}
              </dl>
            </section>
          </main>
        </div>
      </div>
      <SiteFooter />
    </div>
  )
}
